import { logger } from '@dw/services';

/**
 * Evaluated once, because it cannot change at runtime. `mediaDevices` is absent on insecure origins
 * and `MediaRecorder` on a few older browsers. Exported so a caller can decide whether to PROMISE a
 * recorder in its surrounding copy - `VoiceNoteRecorder` renders nothing when this is false, and a
 * caption offering to record above empty space is worse than no caption at all.
 */
export const isVoiceRecordingSupported =
  typeof navigator !== 'undefined' &&
  !!navigator.mediaDevices?.getUserMedia &&
  typeof MediaRecorder !== 'undefined';

/**
 * How many peaks a finished recording is described by. Fixed rather than proportional to duration:
 * it bounds what is stored per note (a 5-minute note costs exactly what a 5-second one does) and it
 * is already finer than the ~100 bars a capsule can draw, so nothing visible is lost.
 */
export const WAVEFORM_PEAK_COUNT = 64;

export const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

/** `0:07`, `1:04`, `12:30` - the shape every voice note in every messenger uses. */
export const formatVoiceNoteTime = (seconds: number): string => {
  const wholeSeconds = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(wholeSeconds / 60)}:${`${wholeSeconds % 60}`.padStart(2, '0')}`;
};

/**
 * The same value spoken rather than punctuated. A screen reader reads `0:07` as "zero colon zero
 * seven", which is not a duration, so the seek slider announces itself with this instead.
 */
export const describeVoiceNoteTime = (seconds: number): string => {
  const wholeSeconds = Math.max(0, Math.round(seconds || 0));
  const minutes = Math.floor(wholeSeconds / 60);
  const remainingSeconds = wholeSeconds % 60;

  const parts: string[] = [];
  if (minutes > 0) {
    parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
  }
  // The seconds half is kept even at zero when there are no minutes, so a silent note still says
  // something ("0 seconds") rather than announcing an empty string.
  if (remainingSeconds > 0 || minutes < 1) {
    parts.push(`${remainingSeconds} second${remainingSeconds === 1 ? '' : 's'}`);
  }

  return parts.join(' ');
};

/**
 * Squeezes an arbitrary peak array onto exactly `barCount` bars by taking the loudest sample in each
 * bar's slice. Max rather than mean, deliberately: averaging flattens speech into a uniform sausage,
 * while the peak is what makes individual syllables visible.
 */
export const resamplePeaks = (peaks: number[], barCount: number): number[] => {
  if (barCount < 1) {
    return [];
  }

  // No peaks at all is a legitimate state (an older note stored before peaks existed, or a decode
  // that failed) and renders as a flat idle field rather than as nothing.
  if (peaks.length < 1) {
    return new Array<number>(barCount).fill(0);
  }

  return Array.from({ length: barCount }, (_unused, index) => {
    const start = Math.floor((index * peaks.length) / barCount);
    // At least one source sample per bar, so upscaling a short peak array repeats values instead of
    // producing empty bars.
    const end = Math.max(start + 1, Math.floor(((index + 1) * peaks.length) / barCount));

    let peak = 0;
    for (let i = start; i < end && i < peaks.length; i++) {
      peak = Math.max(peak, peaks[i]);
    }
    return peak;
  });
};

/** The newest `barCount` samples, right-anchored - silence pads the left until enough have arrived. */
export const tailPeaks = (peaks: number[], barCount: number): number[] => {
  if (barCount < 1) {
    return [];
  }

  const tail = peaks.slice(-barCount);
  return [...new Array<number>(Math.max(0, barCount - tail.length)).fill(0), ...tail];
};

/**
 * Recording containers in preference order, chosen for what the RESULT can be played on rather than
 * for what encodes best.
 *
 * WebM/Opus is the smaller, better-sounding container, and it was tried first here originally - but
 * Safari cannot DECODE it. A note recorded in Chrome was therefore unplayable for a colleague on
 * Safari, which is the failure that matters in a tool people use to talk to each other. mp4 goes
 * first because Safari records it already, Chrome and Edge play it happily, and recent Chrome can
 * record it too; anything that supports none of the mp4 forms falls through to webm exactly as
 * before, so this is strictly better than the old order and regresses nothing.
 *
 * Firefox still records webm only, so a Firefox-to-Safari note remains unplayable. That residual gap
 * is why `VoiceNotePlayer` says so plainly instead of offering a retry that cannot succeed.
 */
const RECORDING_MIME_CANDIDATES = [
  'audio/mp4',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/webm;codecs=opus',
  'audio/webm'
];

/**
 * `null` when the browser admits to none of them - the recorder then lets it pick its own default
 * rather than refusing to record over a failed guess.
 */
export const pickRecordingMimeType = (): string | null =>
  RECORDING_MIME_CANDIDATES.find(candidate => MediaRecorder.isTypeSupported(candidate)) ?? null;

const toRecordingExtension = (mimeType: string): string => {
  if (mimeType.includes('mp4')) {
    return 'm4a';
  }

  if (mimeType.includes('ogg')) {
    return 'ogg';
  }

  return 'webm';
};

/** The API stores this name and any later download uses it, so it has to read as a file, not an id. */
export const buildRecordingFileName = (mimeType: string): string =>
  `voice-note-${Date.now()}.${toRecordingExtension(mimeType)}`;

export interface IDecodedRecording {
  durationSeconds: number;
  waveformPeaks: number[];
}

/**
 * Reads the finished blob back as PCM and reduces it to the fixed peak set the player draws, plus the
 * exact duration the decoder reports.
 *
 * Returns `null` rather than throwing when the browser cannot decode its own recording - it happens,
 * particularly on Safari with very short clips. Peaks and duration are cosmetic and the recorder
 * holds live-sampled fallbacks for both, so a decode failure must never cost the user their audio.
 */
export const decodeRecording = async (blob: Blob): Promise<IDecodedRecording | null> => {
  // A context of its own rather than the capture context: the microphone is released the instant the
  // audio is in hand (a live stream leaves the browser's recording indicator lit, which reads as a
  // bug), and that closes the capture context before this runs.
  //
  // Constructed INSIDE the try, not above it: a browser already at its AudioContext limit - or one
  // where audio output is unavailable - throws on this very line, and a constructor sitting outside
  // the try would break the promise this function's contract makes to never reject.
  let audioContext: AudioContext | null = null;

  try {
    audioContext = new AudioContext();
    const buffer = await audioContext.decodeAudioData(await blob.arrayBuffer());
    const samples = buffer.getChannelData(0);
    const bucketSize = Math.max(1, Math.floor(samples.length / WAVEFORM_PEAK_COUNT));

    let loudest = 0;
    const bucketPeaks = Array.from({ length: WAVEFORM_PEAK_COUNT }, (_unused, index) => {
      const start = index * bucketSize;
      const end = Math.min(start + bucketSize, samples.length);

      let peak = 0;
      for (let i = start; i < end; i++) {
        peak = Math.max(peak, Math.abs(samples[i]));
      }

      loudest = Math.max(loudest, peak);
      return peak;
    });

    return {
      durationSeconds: buffer.duration,
      // Normalised against the loudest bucket rather than against full scale: a note recorded on a
      // quiet laptop mic would otherwise draw as a flat line, and the waveform's whole job is to show
      // the shape of what was said.
      waveformPeaks: bucketPeaks.map(peak =>
        Math.round(clamp(loudest > 0 ? (peak / loudest) * 100 : 0, 0, 100))
      )
    };
  } catch (error) {
    // Logged rather than swallowed: the caller only learns THAT the peaks are missing, so this is the
    // one place the reason reaches New Relic - and an entire browser family failing to read back its
    // own recordings is exactly the sort of thing that should show up as telemetry, not as a ticket.
    logger.error('Error during decodeRecording - could not read the recording back', error);
    return null;
  } finally {
    await audioContext?.close().catch(() => undefined);
  }
};
